import React from 'react';
/**
 * useMobileScanner - Hook for managing barcode scanner operations
 *
 * Handles: Scanner state, barcode reading, item management
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import productService, { BarcodeSearchResult } from '../services/productService';
import { formatDateBR } from '../utils/formatters';

export interface ItemContado {
  id: string;
  codigo: string;
  nome: string;
  unidade: string;
  quantidade: number;
  timestamp: string;
}

// Mock data for simulation
const produtosMock = [
  { codigo: '7891234567890', nome: 'Dipirona 500mg', unidade: 'Ampola' },
  { codigo: '7891234567891', nome: 'Soro Fisiológico 0,9%', unidade: 'Frasco 500ml' },
  { codigo: '7891234567892', nome: 'Luva Procedimento M', unidade: 'Caixa' },
  { codigo: '7891234567893', nome: 'Máscara N95', unidade: 'Unidade' },
  { codigo: '7891234567894', nome: 'Álcool 70% Gel', unidade: 'Frasco' },
  { codigo: '7891234567895', nome: 'Gaze Estéril', unidade: 'Pacote' },
  { codigo: '7891234567896', nome: 'Atadura Crepe', unidade: 'Rolo' }
];

export interface UseMobileScannerReturn {
  // Scanner state
  scannerAtivo: boolean;
  setScannerAtivo: (ativo: boolean) => void;
  codigoBarras: string;
  setCodigoBarras: (codigo: string) => void;
  ultimaLeitura: string;
  feedbackLeitura: boolean;
  codigoBarrasInputRef: React.RefObject<HTMLInputElement>;
  error: string;
  setError: (error: string) => void;

  // Items
  itensEscaneados: ItemContado[];
  itensContados: ItemContado[];
  setItensContados: (itens: ItemContado[]) => void;

  // Operations
  handleSimularLeitura: () => void;
  handleBuscarProdutoPorCodigo: (codigo: string) => Promise<void>;
  handleEditarQuantidadeEscaneado: (id: string, quantidade: number) => void;
  handleRemoverItemEscaneado: (id: string) => void;
  handleRemoverItem: (id: string) => void;
  handleConfirmarItensEscaneados: () => void;
  closeScanner: () => void;
}

// Audio beep function
const playBeep = () => {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.value = 800;
  oscillator.type = 'sine';
  gainNode.gain.value = 0.3;

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.1);
};

export const useMobileScanner = (): UseMobileScannerReturn => {
  // Scanner state
  const [scannerAtivo, setScannerAtivo] = useState(false);
  const [codigoBarras, setCodigoBarras] = useState<string>('');
  const [ultimaLeitura, setUltimaLeitura] = useState<string>('');
  const [feedbackLeitura, setFeedbackLeitura] = useState(false);
  const [error, setError] = useState<string>('');

  // Items
  const [itensEscaneados, setItensEscaneados] = useState<ItemContado[]>([]);
  const [itensContados, setItensContados] = useState<ItemContado[]>([]);

  // Ref
  const codigoBarrasInputRef = useRef<HTMLInputElement>(null);

  // Keep focus on input when scanner is active
  useEffect(() => {
    if (scannerAtivo && codigoBarrasInputRef.current) {
      codigoBarrasInputRef.current.focus();
    }
  }, [scannerAtivo, itensEscaneados]);

  // Simulate barcode reading (mock)
  const handleSimularLeitura = useCallback(() => {
    const produto = produtosMock[Math.floor(Math.random() * produtosMock.length)];

    playBeep();
    setCodigoBarras(produto.codigo);
    setUltimaLeitura(produto.nome);

    setTimeout(() => {
      // Check if product exists
      const itemExistente = itensEscaneados.find(item => item.codigo === produto.codigo);

      if (itemExistente) {
        setItensEscaneados(prev => prev.map(item =>
          item.codigo === produto.codigo
            ? { ...item, quantidade: item.quantidade + 1, timestamp: new Date().toLocaleString('pt-BR') }
            : item
        ));
      } else {
        const novoItem: ItemContado = {
          id: Date.now().toString(),
          codigo: produto.codigo,
          nome: produto.nome,
          unidade: produto.unidade,
          quantidade: 1,
          timestamp: new Date().toLocaleString('pt-BR')
        };
        setItensEscaneados(prev => [...prev, novoItem]);
      }

      setFeedbackLeitura(true);
      setTimeout(() => setFeedbackLeitura(false), 600);
      setCodigoBarras('');
      setUltimaLeitura('');

      setTimeout(() => {
        if (codigoBarrasInputRef.current) {
          codigoBarrasInputRef.current.focus();
        }
      }, 100);
    }, 100);
  }, [itensEscaneados]);

  // Search product by barcode (GS1)
  const handleBuscarProdutoPorCodigo = useCallback(async (codigo: string) => {
    if (!codigo || codigo.trim() === '') return;

    try {
      const resultado: BarcodeSearchResult = await productService.searchProductByBarcode(codigo);
      const produto = resultado.product;

      let descricaoCompleta = produto.nome;
      const dadosGS1: string[] = [];

      if (resultado.lote) {
        dadosGS1.push(`Lote: ${resultado.lote}`);
      }
      if (resultado.data_validade) {
        const dataFormatada = formatDateBR(resultado.data_validade);
        dadosGS1.push(`Val: ${dataFormatada}`);
      }
      if (resultado.numero_serie) {
        dadosGS1.push(`S/N: ${resultado.numero_serie}`);
      }

      if (dadosGS1.length > 0) {
        descricaoCompleta = `${produto.nome}\n📦 ${dadosGS1.join(' | ')}`;
      }

      playBeep();

      const novoItem: ItemContado = {
        id: Date.now().toString(),
        codigo: produto.codigo,
        nome: descricaoCompleta,
        unidade: produto.unidade,
        quantidade: 1,
        timestamp: new Date().toLocaleString('pt-BR')
      };

      const itemExistente = itensEscaneados.find(item => item.codigo === produto.codigo);

      if (itemExistente) {
        setItensEscaneados(prev => prev.map(item =>
          item.codigo === produto.codigo
            ? { ...item, quantidade: item.quantidade + 1, timestamp: new Date().toLocaleString('pt-BR') }
            : item
        ));
      } else {
        setItensEscaneados(prev => [...prev, novoItem]);
      }

      setFeedbackLeitura(true);
      setTimeout(() => setFeedbackLeitura(false), 600);
      setCodigoBarras('');
      setUltimaLeitura('');

      if (codigoBarrasInputRef.current) {
        codigoBarrasInputRef.current.focus();
      }

    } catch (err: any) {
      console.error('❌ Erro ao buscar produto GS1:', err);
      setError(err.message || 'Produto não encontrado');
      setTimeout(() => setError(''), 3000);
    }
  }, [itensEscaneados]);

  // Edit scanned item quantity
  const handleEditarQuantidadeEscaneado = useCallback((id: string, novaQuantidade: number) => {
    if (novaQuantidade < 1) return;
    setItensEscaneados(prev => prev.map(item =>
      item.id === id ? { ...item, quantidade: novaQuantidade } : item
    ));
  }, []);

  // Remove scanned item
  const handleRemoverItemEscaneado = useCallback((id: string) => {
    setItensEscaneados(prev => prev.filter(item => item.id !== id));
  }, []);

  // Remove counted item
  const handleRemoverItem = useCallback((id: string) => {
    setItensContados(prev => prev.filter(item => item.id !== id));
  }, []);

  // Confirm scanned items to main list
  const handleConfirmarItensEscaneados = useCallback(() => {
    if (itensEscaneados.length === 0) return;

    itensEscaneados.forEach(itemEscaneado => {
      const itemExistente = itensContados.find(item => item.codigo === itemEscaneado.codigo);

      if (itemExistente) {
        setItensContados(prev => prev.map(item =>
          item.codigo === itemEscaneado.codigo
            ? { ...item, quantidade: item.quantidade + itemEscaneado.quantidade, timestamp: new Date().toLocaleString('pt-BR') }
            : item
        ));
      } else {
        setItensContados(prev => [itemEscaneado, ...prev]);
      }
    });

    setItensEscaneados([]);
    setScannerAtivo(false);
    setUltimaLeitura('');
    setFeedbackLeitura(false);
    setCodigoBarras('');
  }, [itensEscaneados, itensContados]);

  // Close scanner
  const closeScanner = useCallback(() => {
    setScannerAtivo(false);
    setItensEscaneados([]);
    setUltimaLeitura('');
    setFeedbackLeitura(false);
    setCodigoBarras('');
  }, []);

  return {
    scannerAtivo,
    setScannerAtivo,
    codigoBarras,
    setCodigoBarras,
    ultimaLeitura,
    feedbackLeitura,
    codigoBarrasInputRef,
    error,
    setError,
    itensEscaneados,
    itensContados,
    setItensContados,
    handleSimularLeitura,
    handleBuscarProdutoPorCodigo,
    handleEditarQuantidadeEscaneado,
    handleRemoverItemEscaneado,
    handleRemoverItem,
    handleConfirmarItensEscaneados,
    closeScanner,
  };
};
